import { ChartSvg, ChartTitle } from "@/components/tool-kit/charts/chart-svg";
import type { ChartStyle } from "@/components/tool-kit/charts/controls";
import { SvgLegend } from "@/components/tool-kit/charts/legend";
import { Marker, markerShape, px } from "@/components/tool-kit/charts/marks";
import { colorIndex, paletteColor } from "@/lib/tool-kit/charts/color";
import { type NumberFormat, valueText } from "@/lib/tool-kit/charts/format";
import {
  CHART_FONT_SIZE,
  CHART_PADDING,
  CHART_WIDTH,
  LINE_HEIGHT,
  layoutLegend,
  layoutTitle,
} from "@/lib/tool-kit/charts/layout";
import { maxTextWidth, wrapText } from "@/lib/tool-kit/charts/text";
import {
  labelAnchor,
  layoutAxisLabels,
  layoutRadar,
  type RadarScale,
  type RadarScaling,
} from "@/lib/tool-kit/color-charts/radar";
import { CHART_INK } from "@/lib/tool-kit/palette";

/*
 * A radar (spider) chart drawn as SVG.
 *
 * The drawing is laid out in two passes. The first places the axis names around a circle
 * of known radius with the centre at the origin, which tells us how far the names reach
 * above and below it; the second puts the centre where the reach fits, so a long name at
 * the top or a two-line name at the bottom never runs off the image.
 */

export interface RadarSeries {
  name: string;
  values: (number | null)[];
}

const LABEL_SIZE = CHART_FONT_SIZE.tick;
const LABEL_LINE = LABEL_SIZE * LINE_HEIGHT;
/** Space between a spoke's end and its name; wider when values are printed at the rim. */
const LABEL_GAP = 12;
const LABEL_GAP_WITH_VALUES = 26;
const LABEL_WIDTH = 130;
const MAX_RADIUS = 150;
const MIN_RADIUS = 70;
const VALUE_SIZE = CHART_FONT_SIZE.value;
const HALO = {
  stroke: CHART_INK.background,
  strokeWidth: 3,
  paintOrder: "stroke",
};

export function radarAxisLines(labels: readonly string[]): string[][] {
  return labels.map((label) =>
    wrapText(label, LABEL_WIDTH, { fontSize: LABEL_SIZE }, 2)
  );
}

export function RadarChart({
  id,
  axes,
  series,
  scaling,
  scale,
  fill,
  style,
  fmt,
  label,
  description,
  axisMaxText,
}: {
  id: string;
  /** The axis names, in the order drawn. */
  axes: string[];
  series: RadarSeries[];
  scaling: RadarScaling;
  scale: RadarScale;
  fill: boolean;
  style: ChartStyle;
  fmt: NumberFormat;
  label: string;
  description: string;
  /** Builds the axis name for per-axis scaling: "Speed (100% = 9)". */
  axisMaxText: (axis: string, max: string) => string;
}) {
  const count = axes.length;
  const gap = style.valueLabels ? LABEL_GAP_WITH_VALUES : LABEL_GAP;
  const startColor = colorIndex(style.color);
  const rawMax = layoutRadar({
    series: series.map((s) => s.values),
    axisCount: count,
    scaling,
    scale,
    cx: 0,
    cy: 0,
    radius: 1,
  }).axisMax;
  const names = axes.map((axis, index) =>
    scaling === "perAxis"
      ? axisMaxText(axis, valueText(fmt, rawMax[index] ?? 0))
      : axis
  );
  const lines = radarAxisLines(names);
  const widest = maxTextWidth(lines.flat(), { fontSize: LABEL_SIZE });
  const radius = Math.max(
    MIN_RADIUS,
    Math.min(MAX_RADIUS, CHART_WIDTH / 2 - CHART_PADDING - gap - widest)
  );
  const boxes = layoutAxisLabels(lines, radius, {
    fontSize: LABEL_SIZE,
    lineHeight: LABEL_LINE,
    gap,
  });
  const reachTop = Math.min(-radius, ...boxes.map((box) => box.top));
  const reachBottom = Math.max(radius, ...boxes.map((box) => box.bottom));

  const title = layoutTitle(style.apa ? "" : style.title, CHART_WIDTH);
  const legend =
    series.length > 1
      ? layoutLegend(
          series.map((s) => s.name),
          CHART_WIDTH - 2 * CHART_PADDING
        )
      : null;
  let top = CHART_PADDING + (title?.height ?? 0);
  const legendTop = top;
  if (legend) {
    top += legend.height + 8;
  }
  const cx = CHART_WIDTH / 2;
  const cy = top - reachTop + 4;
  const height = Math.ceil(cy + reachBottom + CHART_PADDING);

  const layout = layoutRadar({
    series: series.map((s) => s.values),
    axisCount: count,
    scaling,
    scale,
    cx,
    cy,
    radius,
  });

  return (
    <ChartSvg
      description={description}
      height={height}
      id={id}
      label={label}
      width={CHART_WIDTH}
    >
      <ChartTitle title={title} />
      {legend ? (
        <SvgLegend
          keys={series.map((_, i) => ({
            color: paletteColor(startColor, i),
            kind: "line",
            shape: markerShape(i),
          }))}
          layout={legend}
          x={CHART_PADDING}
          y={legendTop}
        />
      ) : null}
      <g>
        {layout.rings.map((ring, index) => (
          <polygon
            fill="none"
            key={ring.fraction}
            points={ring.corners.map((c) => `${px(c.x)},${px(c.y)}`).join(" ")}
            stroke={
              index === layout.rings.length - 1
                ? CHART_INK.axis
                : CHART_INK.grid
            }
            strokeWidth={1}
          />
        ))}
        {layout.spokes.map((spoke) => (
          <line
            key={spoke.angle}
            stroke={CHART_INK.grid}
            strokeWidth={1}
            x1={px(cx)}
            x2={px(spoke.x)}
            y1={px(cy)}
            y2={px(spoke.y)}
          />
        ))}
        {layout.rings.map((ring) => (
          <text
            fill={CHART_INK.muted}
            fontSize={VALUE_SIZE}
            key={`t-${ring.fraction}`}
            x={px(cx + 4)}
            y={px(cy - radius * ring.fraction - 2)}
            {...HALO}
          >
            {scaling === "perAxis"
              ? fmt(ring.value / 100, { style: "percent" })
              : valueText(fmt, ring.value)}
          </text>
        ))}
      </g>
      {layout.shapes.map((shape, index) => {
        const color = paletteColor(startColor, index);
        return (
          <g key={series[index]?.name ?? index}>
            <path
              d={shape.path}
              fill={fill && shape.closed ? color : "none"}
              fillOpacity={0.16}
              stroke={color}
              strokeLinejoin="round"
              strokeWidth={2}
            />
            {shape.points.map((point, axis) =>
              point ? (
                <Marker
                  fill={color}
                  key={axes[axis]}
                  shape={markerShape(index)}
                  size={8}
                  x={point.x}
                  y={point.y}
                />
              ) : null
            )}
          </g>
        );
      })}
      {style.valueLabels
        ? layout.shapes.flatMap((shape, index) =>
            shape.points.map((point, axis) =>
              point ? (
                <text
                  fill={CHART_INK.text}
                  fontSize={VALUE_SIZE}
                  key={`v-${series[index]?.name}-${axes[axis]}`}
                  textAnchor={labelAnchor(point.angle)}
                  x={px(point.x + Math.cos(point.angle) * 9)}
                  y={px(
                    point.y + Math.sin(point.angle) * 9 + VALUE_SIZE * 0.35
                  )}
                  {...HALO}
                >
                  {valueText(fmt, point.value)}
                </text>
              ) : null
            )
          )
        : null}
      <g>
        {boxes.map((box, axis) => (
          <text
            fill={CHART_INK.text}
            fontSize={LABEL_SIZE}
            key={axes[axis]}
            textAnchor={box.anchor}
            x={px(cx + box.x)}
            y={px(cy + box.y)}
          >
            {box.lines.map((line, row) => (
              <tspan
                dy={row === 0 ? 0 : LABEL_LINE}
                key={line}
                x={px(cx + box.x)}
              >
                {line}
              </tspan>
            ))}
          </text>
        ))}
      </g>
    </ChartSvg>
  );
}
